<template>
  <div class="fixed top-0 left-0 w-screen h-full bg-white">
    <page class="h-full">
      <template #leftBtn>
        <img
          src="../../../assets/common/back-icon.svg"
          alt="back"
          @click="$emit('update:is-show', false)"
        />
      </template>
      <template #rightBtn>
        <img
          src="../../../assets/common/close-icon.svg"
          alt="menu"
          @click="$emit('clear-active-page')"
        />
      </template>
      <template #content>
        <div class="title text-xl">{{ $t('login.forget_your_password') }}</div>
        <div class="mb-5 text-xl">{{ $t('login.forget_password_text') }}</div>
        <!-- email -->
        <div v-if="type === 'email'" class="input-wrapper relative mb-3">
          <input
            class="w-full h-12 px-6 outline-none"
            type="text"
            autofocus
            v-model="account"
            :placeholder="$t('login.enter_email_address')"
          />
        </div>
        <!-- phone -->
        <div v-else class="phone-input-wrapper flex mb-5">
          <div class="w-20 bg-transparent inline-flex items-center" @click="$emit('edit-code')">
            <span class="inline-block ml-4">
              {{ countryCode.value }}
            </span>
            <img class="ml-2" src="../../../assets/common/chevron-down.svg" />
          </div>
          <input
            class="flex-1 h-12 px-6"
            type="text"
            autofocus
            v-model="account"
            :placeholder="$t('login.enter_phone_number')"
            inputmode="numeric"
          />
        </div>
        <div class="text mb-24 text-xs">{{ promptText }}</div>
        <c-button :confirmed-status="confirmedStatus" class="relative" @click="send">
          <span>{{ $t('login.send') }}</span>
        </c-button>
        <error-message v-if="errorMsg" :message="errorMsg" />
      </template>
    </page>
    <confirm-token
      v-if="showConfirmToken"
      :phone-number="phoneNumber"
      :show-confirm-token.sync="showConfirmToken"
      :error="wrongToken"
      @token="confirmToken"
      @set-password-page="setPasswordPage"
      @confirmed-phone-number="reqResetPwd"
      @clear-active-page="$emit('clear-active-page')"
    />
    <reset-password
      v-if="showResetPassword"
      :phone-number="phoneNumber"
      :token.sync="token"
      :show-reset-password.sync="showResetPassword"
      @set-reset-page="setResetPage"
      @clear-active-page="$emit('clear-active-page')"
    />
    <complete-reset-pwd
      v-if="complete"
      :email="account"
      :type="type"
      @send="send"
      @set-show="$emit('update:is-show', $event)"
    />
  </div>
</template>

<script>
import ApiActions from '@/modules/api-actions';
import Page from '@/components/common/Page.vue';
import CButton from '@/components/common/CButton.vue';
import CompleteResetPwd from './CompleteResetPwd.vue';
import ConfirmToken from './ConfirmToken.vue';
import ResetPassword from './ResetPassword.vue';
import ErrorMessage from '../common/ErrorMessage.vue';

const PROVIDER = 0;

export default {
  components: {
    Page,
    CButton,
    CompleteResetPwd,
    ConfirmToken,
    ResetPassword,
    ErrorMessage,
  },
  props: {
    isShow: Boolean,
    type: String,
    countryCode: Object,
  },
  data() {
    return {
      account: '',
      isHide: true,
      errorMsg: '',
      complete: false,
      showConfirmToken: false,
      showResetPassword: false,
      token: '',
      wrongToken: false,
    };
  },
  computed: {
    phoneNumber() {
      let phoneNumber = this.account;
      if (phoneNumber.indexOf('0') === 0) {
        phoneNumber = phoneNumber.substring(1);
      }
      return `${this.countryCode.value}${phoneNumber}`;
    },
    confirmedStatus() {
      if (this.type === 'email') {
        const emailRegExp = /^\w+((-\w+)|(\.\w+)|(\+\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/;
        return emailRegExp.test(this.account);
      }

      return this.account.length > 3;
    },
    promptText() {
      if (this.type === 'email') {
        return this.$t('login.email_prompt_text');
      }

      return this.$t('login.phone_prompt_text');
    },
  },
  methods: {
    async send() {
      if (!this.confirmedStatus) return;

      this.$events.startLoading();
      await this.reqResetPwd();
      this.$events.stopLoading();
    },
    async confirmToken(token) {
      if (!token) return;

      this.$events.startLoading();

      try {
        await ApiActions.user.confirmPhonePassword({
          confirm_token: token,
          provider: PROVIDER,
          username: this.phoneNumber,
        });
        this.token = token;
        this.setPasswordPage();
      } catch (e) {
        console.log(e.message);
        this.wrongToken = true;
      }
      this.$events.stopLoading();
    },
    async reqResetPwd() {
      const req = { provider: PROVIDER };

      if (this.type === 'phone') {
        req.username = this.phoneNumber;
        req.locale = this.countryCode.code;
      } else {
        req.username = this.account;
      }

      try {
        await ApiActions.user.requestResetPassword(req);
        if (this.type === 'phone') {
          this.showConfirmToken = true;
        } else {
          this.complete = true;
        }
      } catch (e) {
        const { response } = e;
        if (response) {
          const { status, data } = response;
          this.errorMsg = status === 404 ? this.$i18n.t('login.no_such_user') : data.error.msg;
        } else {
          this.errorMsg = this.$i18n.t('welcome.unexpected_error');
        }
        console.log(e.message);
      }
    },
    setPasswordPage() {
      this.showConfirmToken = false;
      this.showResetPassword = true;
    },
    setResetPage() {
      this.showResetPassword = false;
      this.complete = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  width: 309px;

  input {
    @apply bg-no-repeat;
    @apply bg-contain;
    background-color: #f5f5f5;
    &:focus {
      @apply bg-white;
      background-image: url('../../../assets/common/rectangle-1334.svg');
    }
  }
}

.phone-input-wrapper {
  @apply rounded;
  @apply bg-no-repeat;
  @apply bg-contain;
  @apply bg-white;
  background-image: url('../../../assets/common/rectangle-1334.svg');
  width: 309px;

  input {
    outline: none;
    background: none;
  }
}

.text {
  color: #6a6a6a;
}
</style>
